(() => {
  const types = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const maxSourceBytes = 30 * 1024 * 1024;
  const maxPreparedBytes = 1.4 * 1024 * 1024;
  const maxPhotoEdge = 2048;
  const money = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', maximumFractionDigits: 0 });
  const draftDatabase = 'regear-estimator-drafts';
  const draftStore = 'collections';
  const draftMaxAge = 24 * 60 * 60 * 1000;
  let turnstileScriptPromise;
  let heicScriptPromise;

  function openDraftDatabase() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) { reject(new Error('Draft storage is unavailable.')); return; }
      const request = indexedDB.open(draftDatabase, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(draftStore, { keyPath: 'id' });
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async function draftRequest(mode, action) {
    const database = await openDraftDatabase();
    try {
      return await new Promise((resolve, reject) => {
        const transaction = database.transaction(draftStore, mode);
        const request = action(transaction.objectStore(draftStore));
        transaction.oncomplete = () => resolve(request.result);
        transaction.onabort = () => reject(transaction.error);
        transaction.onerror = () => reject(transaction.error || request.error);
      });
    } finally { database.close(); }
  }

  function loadTurnstileScript() {
    if (window.turnstile) return Promise.resolve();
    if (turnstileScriptPromise) return turnstileScriptPromise;
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true; script.defer = true; script.onload = resolve; script.onerror = reject;
      document.head.appendChild(script);
    });
    return turnstileScriptPromise;
  }

  function isHeic(file) {
    return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
  }

  function isPhoto(file) {
    return types.includes(file.type) || /\.(jpe?g|png|webp|hei[cf])$/i.test(file.name);
  }

  function loadHeicScript(url) {
    if (window.heic2any) return Promise.resolve();
    if (heicScriptPromise) return heicScriptPromise;
    heicScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url; script.async = true; script.onload = resolve;
      script.onerror = () => reject(new Error('HEIC photo support could not be loaded. Please retry or choose JPG photos.'));
      document.head.appendChild(script);
    });
    return heicScriptPromise;
  }

  async function decodePhoto(blob) {
    if ('createImageBitmap' in window) {
      try {
        const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
        return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
      } catch { /* use the broadly compatible image-element fallback */ }
    }
    const url = URL.createObjectURL(blob);
    try {
      const image = await new Promise((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('This photo could not be opened.'));
        element.src = url;
      });
      return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) };
    } catch (error) {
      URL.revokeObjectURL(url);
      throw error;
    }
  }

  async function preparePhoto(file, heicUrl, updateStatus = () => {}) {
    if (!isPhoto(file) || file.size > maxSourceBytes) throw new Error(`${file.name} is not a supported photo or is larger than 30 MB.`);
    updateStatus('Preparing…');
    let sourceBlob = file;
    if (isHeic(file)) {
      updateStatus('Converting iPhone photo…');
      await loadHeicScript(heicUrl);
      const converted = await window.heic2any({ blob: file, toType: 'image/jpeg', quality: .92 });
      sourceBlob = Array.isArray(converted) ? converted[0] : converted;
    } else if (file.size <= maxPreparedBytes) {
      updateStatus('Ready');
      return file;
    }

    const decoded = await decodePhoto(sourceBlob);
    try {
      let scale = Math.min(1, maxPhotoEdge / Math.max(decoded.width, decoded.height));
      let quality = .88;
      let output = null;
      for (let attempt = 0; attempt < 7; attempt += 1) {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(decoded.width * scale));
        canvas.height = Math.max(1, Math.round(decoded.height * scale));
        const context = canvas.getContext('2d', { alpha: false });
        context.fillStyle = '#fff'; context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);
        output = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
        if (!output) throw new Error(`${file.name} could not be prepared.`);
        if (output.size <= maxPreparedBytes) break;
        quality = Math.max(.56, quality - .08);
        scale *= .82;
      }
      if (!output || output.size > maxPreparedBytes) throw new Error(`${file.name} is still too large after preparation.`);
      updateStatus('Ready');
      return new File([output], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg', lastModified: file.lastModified });
    } finally {
      decoded.close();
    }
  }

  async function requestWithTimeout(url, options, timeout = 75000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    try { return await fetch(url, { ...options, signal: controller.signal, credentials: options?.credentials ?? 'omit' }); }
    finally { clearTimeout(timer); }
  }

  function paragraph(text, className = '') {
    const element = document.createElement('p');
    element.textContent = text;
    if (className) element.className = className;
    return element;
  }

  function init(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';
    const api = root.dataset.api.replace(/\/$/, '');
    const heicUrl = root.dataset.heicScript;
    const form = root.querySelector('[data-estimator-form]');
    const photos = root.querySelector('[data-estimator-photos]');
    const dropzone = root.querySelector('[data-estimator-dropzone]');
    const previews = root.querySelector('[data-estimator-previews]');
    const photoCount = root.querySelector('[data-estimator-photo-count]');
    const error = root.querySelector('[data-estimator-error]');
    const result = root.querySelector('[data-estimator-result]');
    const resultLabel = root.querySelector('[data-estimator-result-label]');
    const price = root.querySelector('[data-estimator-price]');
    const explanation = root.querySelector('[data-estimator-explanation]');
    const review = root.querySelector('[data-estimator-review]');
    const sources = root.querySelector('[data-estimator-sources]');
    const interestPanel = root.querySelector('[data-interest-panel]');
    const interestHeading = root.querySelector('[data-interest-heading]');
    const interestCopy = root.querySelector('[data-interest-copy]');
    const leadError = root.querySelector('[data-interest-error]');
    const success = root.querySelector('[data-estimator-success]');
    const estimateButton = root.querySelector('[data-estimator-submit]');
    const leadButton = root.querySelector('[data-interest-submit]');
    const collection = root.querySelector('[data-gear-collection]');
    const collectionList = root.querySelector('[data-collection-list]');
    const collectionCount = root.querySelector('[data-collection-count]');
    const collectionError = root.querySelector('[data-collection-error]');
    const collectionAdd = root.querySelector('[data-collection-add]');
    const collectionClear = root.querySelector('[data-collection-clear]');
    const collectionSubmit = root.querySelector('[data-collection-submit]');
    const sellerName = root.querySelector('[data-interest-name]');
    const contactEmail = root.querySelector('[data-interest-email]');
    const marketingConsent = root.querySelector('[data-interest-marketing]');
    const turnstileHost = root.querySelector('[data-estimator-turnstile]');
    const nativeMarketingForm = root.querySelector('form[id^="rg-estimator-marketing-"]');
    const savedCopy = root.dataset.savedCopy;
    const resultCopy = JSON.parse(root.dataset.resultCopy || '{}');
    const compatibilityError = root.dataset.compatibilityError;
    const successCopy = root.dataset.successCopy;
    let backendCompatible = false;
    estimateButton.disabled = true;
    const progress = root.querySelector('[data-estimator-progress]');
    const progressBar = root.querySelector('[data-estimator-progress-bar]');
    const progressPercent = root.querySelector('[data-estimator-progress-percent]');
    const morePhotos = root.querySelector('[data-estimator-more-photos]');
    let sourceFiles = [];
    let prepared = [];
    let estimateId = '';
    let urls = [];
    let progressValue = 0;
    let leadButtonText = 'Add this item to my gear list';
    let interestType = 'cash_purchase';
    let currentEstimateData = null;
    let savedItems = [];
    let pendingCollectionId = '';
    let collectionToken = '';
    let sendingCollection = false;
    let handoffStatus = {};
    let turnstileRequired = false;
    let turnstileToken = '';
    let turnstileWidgetId = null;
    let backendEmailConfigured = false;
    let dragDepth = 0;
    const estimatorSessionId = (() => {
      try {
        const key = 'regear-estimator-session';
        const existing = localStorage.getItem(key);
        if (existing) return existing;
        const created = crypto.randomUUID(); localStorage.setItem(key, created); return created;
      } catch { return crypto.randomUUID(); }
    })();

    const persistDraftCollection = async () => {
      try {
        if (!savedItems.length) { await draftRequest('readwrite', (store) => store.delete('current')); return; }
        const items = savedItems.map(({ previewUrl, ...item }) => ({ ...item }));
        await draftRequest('readwrite', (store) => store.put({ id: 'current', savedAt: Date.now(), pendingCollectionId, collectionToken, handoffStatus, sellerName: sellerName.value, email: contactEmail.value, marketingConsent: marketingConsent.checked, items }));
      } catch { showCollectionError('Your browser could not save this list on your device. Keep this page open until you have sent it.'); }
    };
    const restoreDraftCollection = async () => {
      try {
        const draft = await draftRequest('readonly', (store) => store.get('current'));
        if (!draft?.items?.length || Date.now() - draft.savedAt > draftMaxAge) { await draftRequest('readwrite', (store) => store.delete('current')); return; }
        savedItems = draft.items.slice(0, 10).map((item) => {
          const files = (item.files || []).map((file, index) => file instanceof File ? file : new File([file], `saved-photo-${index + 1}.jpg`, { type: file.type || 'image/jpeg' }));
          return { ...item, files, status: item.status || 'ready', previewUrl: files[0] ? URL.createObjectURL(files[0]) : '' };
        }).filter((item) => item.files.length && item.estimateId);
        pendingCollectionId = draft.pendingCollectionId || '';
        collectionToken = draft.collectionToken || '';
        handoffStatus = draft.handoffStatus || {};
        sellerName.value = draft.sellerName || ''; contactEmail.value = draft.email || ''; marketingConsent.checked = draft.marketingConsent === true;
        if (savedItems.length) renderCollection();
      } catch { /* A saved draft is a convenience, never a submission dependency. */ }
    };
    const resetTurnstile = () => {
      turnstileToken = '';
      if (turnstileWidgetId !== null && window.turnstile) window.turnstile.reset(turnstileWidgetId);
    };
    const postShopifyForm = async (nativeForm) => {
      const response = await requestWithTimeout(nativeForm.action, { method: 'POST', body: new FormData(nativeForm), credentials: 'same-origin', headers: { accept: 'text/html' } }, 20000);
      if (!response.ok) throw new Error('Shopify form submission failed.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!html.getElementById(nativeForm.id)?.querySelector('[data-regear-form-success]')) throw new Error('Your gear is saved, but Shopify has not confirmed the notification. You can retry this final step.');
    };
    const runShopifyHandoffs = async (selected, collectionId) => {
      const tasks = [];
      if (marketingConsent.checked && nativeMarketingForm && !handoffStatus.marketing) {
        nativeMarketingForm.querySelector('[data-native-marketing-email]').value = contactEmail.value.trim();
        tasks.push(postShopifyForm(nativeMarketingForm).then(() => { handoffStatus.marketing = true; }));
      }
      const results = await Promise.allSettled(tasks);
      await persistDraftCollection();
      const record = await requestWithTimeout(`${api}/api/leads/notification-status`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({collectionId, collectionToken, owner: false, marketing: handoffStatus.marketing === true}) }, 15000);
      if (!record.ok) throw new Error('Your items are saved, but the notification status could not be recorded. Please retry the final step.');
      const failed = results.find((r) => r.status === 'rejected');
      if (failed) throw failed.reason;
    };
    const configReady = requestWithTimeout(`${api}/api/config`, {}, 15000).then((response) => { if (!response.ok) throw new Error(compatibilityError); return response.json(); }).then(async (config) => {
      if (config.submissionVersion !== 'secure-v7') throw new Error(compatibilityError);
      backendCompatible = true;
      estimateButton.disabled = false;
      backendEmailConfigured = config.emailNotificationsConfigured === true;
      if (!config.turnstileSiteKey) return;
      turnstileRequired = true; turnstileHost.hidden = false;
      await loadTurnstileScript();
      turnstileWidgetId = window.turnstile.render(turnstileHost, {
        sitekey: config.turnstileSiteKey,
        theme: 'light',
        callback: (token) => { turnstileToken = token; },
        'expired-callback': () => { turnstileToken = ''; },
        'error-callback': () => { turnstileToken = ''; },
      });
    }).catch(() => { backendCompatible = false; estimateButton.disabled = true; showError(compatibilityError); });

    const stopProgress = () => {};
    const setProgress = (percent, stage) => {
      if (percent < progressValue) return;
      const rounded = Math.max(progressValue, Math.min(100, Math.round(percent)));
      progressValue = rounded;
      progress.hidden = false; progress.setAttribute('aria-valuenow', String(rounded));
      progressBar.style.transform = `scaleX(${rounded / 100})`;
      progressPercent.textContent = `${rounded}%`;
      if (stage) progress.querySelector('strong').textContent = stage;
    };
    const startProgress = () => {
      progressValue = 0; setProgress(0, 'Preparing photos');
    };

    const showError = (message) => { error.textContent = message; error.hidden = false; };
    const showLeadError = (message) => { leadError.textContent = message; leadError.hidden = false; };
    const showCollectionError = (message) => { collectionError.textContent = message; collectionError.hidden = false; };
    const renderSourcePhotos = () => {
      urls.forEach(URL.revokeObjectURL); urls = []; previews.replaceChildren();
      sourceFiles.forEach((file, index) => {
        const card = document.createElement('div'); card.className = 'rg-estimator__preview-card';
        const url = URL.createObjectURL(file); urls.push(url);
        const image = new Image(); image.src = url; image.alt = `Selected photo: ${file.name}`;
        const status = document.createElement('span'); status.dataset.photoStatus = String(index); status.textContent = isHeic(file) ? 'iPhone photo selected' : 'Ready to prepare';
        const remove = document.createElement('button'); remove.type = 'button'; remove.textContent = 'Remove'; remove.setAttribute('aria-label', `Remove ${file.name}`);
        remove.addEventListener('click', () => { sourceFiles.splice(index, 1); prepared = []; photos.value = ''; renderSourcePhotos(); });
        card.append(image, status, remove); previews.append(card);
      });
      previews.hidden = !sourceFiles.length;
      photoCount.textContent = sourceFiles.length ? `${sourceFiles.length} of 10 photos selected` : 'No photos selected';
    };
    const setPhotoStatus = (index, message, failed = false) => {
      const target = previews.querySelector(`[data-photo-status="${index}"]`);
      if (!target) return;
      target.textContent = message; target.classList.toggle('is-error', failed);
    };
    const updateCollectionSummary = () => {
      const selected = savedItems.filter((item) => item.selected).length;
      const sent = savedItems.filter((item) => item.selected && item.status === 'sent').length;
      const remaining = savedItems.filter((item) => item.selected && item.status !== 'sent').length;
      collectionCount.textContent = `${savedItems.length} item${savedItems.length === 1 ? '' : 's'} saved · ${selected} selected${sent ? ` · ${sent} sent` : ''}`;
      collectionSubmit.disabled = sendingCollection || selected === 0;
      sellerName.readOnly = Boolean(pendingCollectionId); contactEmail.readOnly = Boolean(pendingCollectionId); marketingConsent.disabled = Boolean(pendingCollectionId);
      collectionSubmit.textContent = selected ? (remaining ? `Send ${remaining} remaining item${remaining === 1 ? '' : 's'} to ReGear` : 'Finish submission') : 'Select at least one item';
      collectionAdd.disabled = Boolean(pendingCollectionId);
      collectionClear.disabled = Boolean(pendingCollectionId);
    };
    const renderCollection = () => {
      collectionList.replaceChildren();
      savedItems.forEach((item, index) => {
        const row = document.createElement('label'); row.className = `rg-estimator__collection-item is-${item.status || 'ready'}`;
        const checkbox = document.createElement('input'); checkbox.type = 'checkbox'; checkbox.checked = item.selected; checkbox.disabled = Boolean(pendingCollectionId); checkbox.setAttribute('aria-label', `Include ${item.title}`);
        checkbox.addEventListener('change', () => { savedItems[index].selected = checkbox.checked; updateCollectionSummary(); void persistDraftCollection(); });
        const image = new Image(); image.src = item.previewUrl; image.alt = '';
        const copy = document.createElement('div');
        const title = document.createElement('strong'); title.textContent = item.title;
        const detail = document.createElement('span'); detail.textContent = item.detail;
        const offer = document.createElement('b'); offer.textContent = item.offerText;
        const state = document.createElement('em'); state.className = 'rg-estimator__collection-state';
        state.textContent = item.status === 'sent' ? 'Sent securely' : item.status === 'sending' ? 'Sending…' : item.status === 'error' ? `Needs retry${item.lastError ? `: ${item.lastError}` : ''}` : 'Ready to send';
        copy.append(title, detail, offer, state); row.append(checkbox, image, copy); collectionList.append(row);
        if (item.description) { const note = document.createElement('span'); note.textContent = item.description; copy.append(note); }
      });
      collection.hidden = savedItems.length === 0;
      collectionError.hidden = true;
      updateCollectionSummary();
    };
    const saveCurrent = () => {
      if (currentEstimateData?.analysis?.multipleItemsDetected) {
        showLeadError('Run a separate estimate for each item before adding it to your gear list.');
        return false;
      }
      if (!estimateId || savedItems.some((item) => item.estimateId === estimateId)) return true;
      if (savedItems.length >= 10) { showLeadError('Your gear list already has 10 items. Send some items before adding more.'); return false; }
      const analysis = currentEstimateData?.analysis || {};
      const title = [analysis.brand, analysis.model].filter(Boolean).join(' · ') || analysis.itemType || 'Outdoor gear';
      const detail = [analysis.itemType, analysis.size ? `Size: ${analysis.size}` : '', analysis.condition ? `Condition: ${analysis.condition}` : '', `${prepared.length} photo${prepared.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ');
      const offerText = ['reject', 'paused', 'manual'].includes(currentEstimateData?.disposition) ? 'ReGear review needed' : currentEstimateData?.offerHigh > 0 ? `${money.format(currentEstimateData.offerLow)}–${money.format(currentEstimateData.offerHigh)}` : currentEstimateData?.alternativeOption === 'consignment_review' ? resultCopy.consignment_title : 'Donate';
      savedItems.push({ estimateId, description: String(new FormData(form).get('description') || '').trim().slice(0, 1000), submissionToken: currentEstimateData.submissionToken, files: [...prepared], previewUrl: prepared[0] ? URL.createObjectURL(prepared[0]) : '', title, detail, offerText, interestType, selected: true, status: 'ready', lastError: '' });
      renderCollection();
      void persistDraftCollection();
      return true;
    };
    const resetItem = () => {
      form.reset(); sourceFiles = []; prepared = []; estimateId = ''; currentEstimateData = null;
      urls.forEach(URL.revokeObjectURL); urls = [];
      previews.replaceChildren(); previews.hidden = true;
      photoCount.textContent = 'No photos selected';
      stopProgress(); progress.hidden = true; progressBar.style.transform = 'scaleX(0)'; progressPercent.textContent = '0%'; progress.setAttribute('aria-valuenow', '0');
      price.classList.remove('rg-estimator__price--message');
      resultLabel.textContent = 'Indicative ReGear purchase range';
      interestHeading.textContent = 'Want to include this gear?';
      interestCopy.textContent = savedCopy;
      leadButtonText = 'Add this item to my gear list'; interestType = 'cash_purchase'; leadButton.textContent = leadButtonText; leadButton.disabled = false;
      result.hidden = true; success.hidden = true; error.hidden = true; sources.hidden = true; interestPanel.hidden = false; form.hidden = false;
      morePhotos.hidden = true; morePhotos.textContent = 'Add the requested photo';
      leadError.hidden = true;
      collection.hidden = savedItems.length === 0;
      resetTurnstile();
      form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const addSourceFiles = (added) => {
      error.hidden = true; prepared = [];
      const invalid = added.find((file) => !isPhoto(file) || file.size > maxSourceBytes);
      if (invalid) {
        photos.value = '';
        showError(`${invalid.name} is not a supported photo or is larger than 30 MB.`); return;
      }
      const merged = [...sourceFiles];
      for (const file of added) {
        if (!merged.some((existing) => existing.name === file.name && existing.size === file.size && existing.lastModified === file.lastModified)) merged.push(file);
      }
      if (merged.length > 10) {
        photos.value = '';
        showError('You can add up to 10 photos for one item. Remove one before adding another.'); return;
      }
      sourceFiles = merged; photos.value = ''; renderSourcePhotos();
    };

    photos.addEventListener('change', () => {
      addSourceFiles(Array.from(photos.files || []));
    });

    dropzone.addEventListener('dragenter', (event) => {
      if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
      event.preventDefault(); dragDepth += 1; dropzone.classList.add('is-dragging');
    });
    dropzone.addEventListener('dragover', (event) => {
      if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
      event.preventDefault(); event.dataTransfer.dropEffect = 'copy';
    });
    dropzone.addEventListener('dragleave', () => {
      dragDepth = Math.max(0, dragDepth - 1);
      if (!dragDepth) dropzone.classList.remove('is-dragging');
    });
    dropzone.addEventListener('drop', (event) => {
      event.preventDefault(); dragDepth = 0; dropzone.classList.remove('is-dragging');
      addSourceFiles(Array.from(event.dataTransfer?.files || []));
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault(); error.hidden = true; success.hidden = true;
      if (!sourceFiles.length) { showError('Add at least one clear photo. All other questions are optional.'); return; }
      if (!form.checkValidity()) { form.reportValidity(); return; }
      await configReady;
      if (!backendCompatible) { showError(compatibilityError); return; }
      if (turnstileRequired && !turnstileToken) { showError('Complete the security check before requesting an estimate.'); return; }
      estimateButton.disabled = true; estimateButton.textContent = 'Identifying and checking NZ prices…';
      startProgress();
      try {
        prepared = [];
        for (let index = 0; index < sourceFiles.length; index += 1) {
          try {
            prepared.push(await preparePhoto(sourceFiles[index], heicUrl, (message) => setPhotoStatus(index, message)));
          } catch (photoError) {
            setPhotoStatus(index, photoError.message || 'Could not prepare', true);
            throw photoError;
          }
        }
        const body = new FormData(form); body.delete('photos'); prepared.forEach((file) => body.append('photos', file));
        body.set('estimatorSessionId', estimatorSessionId);
        if (turnstileToken) body.set('turnstileToken', turnstileToken);
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 300000);
        const progressId = crypto.randomUUID();
        const pollController = new AbortController();
        let polling = true;
        let pollTimer;
        const poll = async () => {
          try {
            const response = await fetch(`${api}/api/estimate?progressId=${progressId}`, {credentials:'omit',cache:'no-store',signal:AbortSignal.any([pollController.signal,AbortSignal.timeout(8000)])});
            if (response.ok) { const status = await response.json(); if (polling && status.percent > 0) setProgress(status.percent,status.stage); }
          } catch {}
          if (polling) pollTimer = setTimeout(poll,3000);
        };
        pollTimer = setTimeout(poll,1500);
        let data;
        try {
          setProgress(1, 'Uploading photos');
          const response = await fetch(`${api}/api/estimate?progress=1&progressId=${progressId}`, { method: 'POST', body, credentials: 'omit', signal: controller.signal });
          if (!/text\/event-stream|application\/x-ndjson/.test(response.headers.get('content-type') || '')) {
            data = await response.json();
            if (!response.ok) throw new Error(data.error || 'The estimate could not be completed.');
          } else {
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            try {
              while (!data) {
                const chunk = await reader.read();
                buffer += decoder.decode(chunk.value, { stream: !chunk.done });
                const lines = buffer.split('\n'); buffer = lines.pop() || '';
                for (const line of lines) {
                  if (!line.trim()) continue;
                  if (line.startsWith(':')) continue;
                  const event = JSON.parse(line.replace(/^data: ?/, ''));
                  if (event.type === 'progress') setProgress(event.percent, event.stage);
                  if (event.type === 'result') {
                    if (event.status >= 400) throw new Error(event.data.error || 'The estimate could not be completed.');
                    data = event.data;
                  }
                }
                if (chunk.done && !data) throw new Error('The connection was interrupted. Your selected photos are still here; please retry.');
              }
            } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
          }
        } finally { clearTimeout(timeout); polling = false; clearTimeout(pollTimer); pollController.abort(); }
        estimateId = data.estimateId; currentEstimateData = data;
        stopProgress(); setProgress(100, 'Estimate ready');
        price.classList.remove('rg-estimator__price--message');
        resultLabel.textContent = 'Indicative ReGear purchase range';
        interestHeading.textContent = 'Want to include this gear?';
        interestCopy.textContent = savedCopy;
        leadButtonText = 'Add this item to my gear list'; interestType = 'cash_purchase';
        morePhotos.hidden = true;
        if (data.disposition === 'reject' || data.disposition === 'paused') {
          price.textContent = 'Not available online'; explanation.textContent = data.intake.note || 'ReGear cannot responsibly accept this category from an online estimate.';
        } else if (data.disposition === 'manual') {
          if (data.analysis?.multipleItemsDetected) {
            price.textContent = 'Please separate these items';
            price.classList.add('rg-estimator__price--message');
            resultLabel.textContent = 'One item per estimate';
            explanation.textContent = data.analysis.multipleItemsNote || 'These photos appear to show more than one product. Run one estimate per item so each price is reliable.';
            morePhotos.textContent = 'Start again with one item';
            morePhotos.hidden = false;
          } else {
            price.textContent = resultCopy.review_title;
            explanation.textContent = data.analysis?.marketSummary || resultCopy.review_explanation;
            morePhotos.hidden = !(data.analysis?.photoRequests?.length);
          }
        } else if (data.offerHigh <= 0 && data.alternativeOption) {
          const consignmentReview = data.alternativeOption === 'consignment_review';
          price.textContent = consignmentReview ? resultCopy.consignment_title : 'Donate this item';
          price.classList.add('rg-estimator__price--message');
          resultLabel.textContent = 'No cash purchase offer';
          const noCashReason = data.noCashReason || 'The likely resale value is not high enough to cover ReGear’s time and costs to check, clean, photograph, list and sell it while still leaving room for a fair cash purchase.';
          explanation.textContent = consignmentReview
            ? `${noCashReason} ${resultCopy.consignment_explanation}`
            : `${noCashReason} ReGear can still accept it as a donation, or review it together with other gear as a bundle.`;
          interestHeading.textContent = consignmentReview ? resultCopy.consignment_heading : 'Add this donation to your gear list';
          interestCopy.textContent = savedCopy;
          leadButtonText = consignmentReview ? resultCopy.consignment_button : 'Add donation to my gear list';
          interestType = data.alternativeOption;
        } else {
          price.innerHTML = `${money.format(data.offerLow)}–${money.format(data.offerHigh)} <span>NZD</span>`;
          explanation.textContent = data.disclaimer || resultCopy.inspection_notice;
        }

        const analysis = data.analysis || {};
        const lines = [
          `Item scanned: ${[analysis.brand, analysis.model].filter(Boolean).join(' · ') || analysis.itemType || 'Outdoor gear'}`,
          [analysis.itemType, analysis.condition ? `Condition: ${analysis.condition}` : ''].filter(Boolean).join(' · '),
          `Size: ${analysis.size || resultCopy.size_unavailable}`,
        ].filter(Boolean).map((line, index) => paragraph(line, index === 0 ? 'rg-estimator__review-title' : ''));
        lines.push(paragraph(resultCopy.condition_notice));
        if (analysis.faults?.length) lines.push(paragraph(`${resultCopy.issues_label}: ${analysis.faults.join(', ')}`));
        if (analysis.missingParts?.length) lines.push(paragraph(`${resultCopy.missing_label}: ${analysis.missingParts.join(', ')}`));
        if (analysis.photoRequests?.length) {
          lines.push(paragraph('Photos that would help:', 'rg-estimator__review-title'));
          analysis.photoRequests.forEach((request) => lines.push(paragraph(`• ${request}`)));
        }
        review.replaceChildren(...lines);

        sources.replaceChildren();
        sources.hidden = true;

        leadButton.textContent = leadButtonText;
        form.hidden = true; result.hidden = false; interestPanel.hidden = Boolean(data.analysis?.multipleItemsDetected);
        result.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (caught) {
        stopProgress(); progress.hidden = true;
        showError(/AbortError|TimeoutError/.test(caught.name) ? 'This estimate took longer than five minutes. Please retry. Your selected photos are still here.' : caught.message);
      } finally {
        resetTurnstile();
        estimateButton.disabled = false; estimateButton.textContent = 'Get my purchase estimate';
      }
    });

    morePhotos.addEventListener('click', () => {
      if (currentEstimateData?.analysis?.multipleItemsDetected) { resetItem(); return; }
      result.hidden = true; form.hidden = false; morePhotos.hidden = true;
      root.querySelector('.rg-estimator__upload').scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    leadButton.addEventListener('click', () => {
      if (!saveCurrent()) return;
      result.hidden = true; form.hidden = true; collection.hidden = false;
      collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    collectionAdd.addEventListener('click', () => { if (!estimateId || saveCurrent()) resetItem(); });

    collectionClear.addEventListener('click', async () => {
      savedItems.forEach((item) => URL.revokeObjectURL(item.previewUrl)); savedItems = []; pendingCollectionId = '';
      await persistDraftCollection(); collection.hidden = true; result.hidden = true; success.hidden = true; form.hidden = false;
      resetItem();
    });

    collectionSubmit.addEventListener('click', async () => {
      if (sendingCollection) return;
      const selected = savedItems.filter((item) => item.selected);
      collectionError.hidden = true;
      if (!selected.length) { showCollectionError('Select at least one saved item to send.'); return; }
      if (!sellerName.value.trim()) { showCollectionError('Add your name so ReGear knows who submitted the collection.'); sellerName.focus(); return; }
      if (!contactEmail.checkValidity()) { showCollectionError('Add a valid email address so ReGear can reply about the collection.'); contactEmail.focus(); return; }
      sendingCollection = true;
      collectionToken ||= crypto.randomUUID();
      collectionSubmit.disabled = true; collectionAdd.disabled = true; collectionClear.disabled = true;
      const collectionId = pendingCollectionId || crypto.randomUUID(); pendingCollectionId = collectionId;
      if (!selected.every((item) => Number.isInteger(item.collectionIndex))) {
        selected.forEach((item, index) => { item.collectionIndex = index + 1; item.collectionSize = selected.length; });
      }
      await persistDraftCollection();
      try {
        const pending = selected.filter((item) => item.status !== 'sent');
        for (let index = 0; index < pending.length; index += 1) {
          const item = pending[index]; item.status = 'sending'; item.lastError = ''; renderCollection();
          collectionSubmit.textContent = `Sending item ${index + 1} of ${pending.length}…`;
          try {
            const body = new FormData();
            body.set('notes', item.description || '');
            body.set('collectionToken', collectionToken); body.set('submissionToken', item.submissionToken || '');
            body.set('estimateId', item.estimateId); body.set('consent', 'yes'); body.set('sellerName', sellerName.value.trim()); body.set('email', contactEmail.value.trim()); body.set('marketingConsent', marketingConsent.checked ? 'yes' : 'no'); body.set('interestType', item.interestType); body.set('collectionId', collectionId); body.set('collectionSize', String(item.collectionSize)); body.set('collectionIndex', String(item.collectionIndex));
            item.files.forEach((file) => body.append('photos', file));
            const response = await requestWithTimeout(`${api}/api/leads`, { method: 'POST', body }, 90000);
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || `Item ${item.collectionIndex} could not be saved.`);
            item.status = 'sent'; await persistDraftCollection(); renderCollection();
          } catch (itemError) {
            item.status = 'error'; item.lastError = itemError.name === 'AbortError' ? 'connection timed out' : itemError.message;
            await persistDraftCollection(); renderCollection();
          }
        }
        const failed = selected.filter((item) => item.status !== 'sent');
        if (failed.length) {
          showCollectionError(`${selected.length - failed.length} of ${selected.length} items are safely received. ${failed.length} still need to be retried; the received items will not be duplicated.`);
          return;
        }
        await runShopifyHandoffs(selected, collectionId);
        selected.forEach((item) => URL.revokeObjectURL(item.previewUrl)); savedItems = savedItems.filter((item) => !selected.includes(item)); pendingCollectionId = ''; collectionToken = ''; handoffStatus = {}; await persistDraftCollection();
        renderCollection(); form.hidden = true; result.hidden = true;
        success.textContent = successCopy;
        success.hidden = false; success.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } catch (caught) {
        showCollectionError(caught.name === 'AbortError' ? 'Sending the collection took longer than expected. Please retry.' : caught.message);
      } finally {
        sendingCollection = false;
        updateCollectionSummary();
      }
    });

    if (new URLSearchParams(window.location.search).get('regear_preview') === 'low-value') {
      resultLabel.textContent = 'No cash purchase offer';
      price.textContent = 'Donate this item';
      price.classList.add('rg-estimator__price--message');
      explanation.textContent = 'Its likely resale value is not high enough to cover ReGear’s time and costs to check, clean, photograph, list and sell it while still leaving room for a fair cash purchase. ReGear can still accept it as a donation, or review it together with other gear as a bundle.';
      review.replaceChildren(
        paragraph('Item scanned: Mountain Warehouse · children’s insulated vest', 'rg-estimator__review-title'),
        paragraph('Outdoor clothing · Condition: excellent'),
        paragraph('Size: Children’s 9–10 years'),
      );
      interestHeading.textContent = 'Add this donation to your gear list';
      interestCopy.textContent = savedCopy;
      leadButton.textContent = 'Preview only — no submission'; leadButton.disabled = true;
      form.hidden = true; result.hidden = false; sources.hidden = true;
    } else { void restoreDraftCollection(); }

    root.querySelector('[data-estimator-reset]').addEventListener('click', () => { if (saveCurrent()) resetItem(); });
  }

  const start = () => document.querySelectorAll('[data-regear-estimator]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', start);
})();
